import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, MapPin, Clock, Plus, Loader2, Eye, Sparkles, BadgeCheck } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AnnoncesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [annonces, setAnnonces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
  });

  useEffect(() => {
    fetchCategories();
    fetchAnnonces();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAnnonces();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAnnonces = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.location) params.append('location', filters.location);
      
      const response = await axios.get(`${API_URL}/annonces?${params.toString()}`);
      setAnnonces(response.data);
    } catch (error) {
      console.error('Error fetching annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', location: '' });
    setSearchParams({});
  };

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Annonces</h1>
            <p className="text-muted-foreground">
              Decouvrez les offres de la communauté et trouvez votre match
            </p>
          </div>
          {isAuthenticated && (
            <Link to="/annonces/new">
              <Button className="btn-primary" data-testid="new-annonce-btn">
                <Plus className="w-4 h-4 mr-2" />
                Créer une annonce
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <Card className="rounded-2xl mb-8">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Rechercher une annonce..."
                  className="pl-10 rounded-xl"
                  data-testid="search-input"
                />
              </div>

              {/* Category Filter */}
              <Select
                value={filters.category}
                onValueChange={(value) => handleFilterChange('category', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-full md:w-48 rounded-xl">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.slug}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Location Filter */}
              <div className="relative w-full md:w-48">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  placeholder="Localisation"
                  className="pl-10 rounded-xl"
                />
              </div>

              {/* Clear Filters */}
              {(filters.search || filters.category || filters.location) && (
                <Button variant="ghost" onClick={clearFilters} className="rounded-xl">
                  Effacer
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            {annonces.length} annonce(s) trouvee(s)
          </p>
        </div>

        {/* Annonces Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : annonces.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {annonces.map((annonce) => (
              <Link key={annonce.id} to={`/annonces/${annonce.id}`}>
                <Card className="h-full rounded-xl hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                  <CardContent className="p-5">
                    {/* User */}
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={annonce.user?.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {annonce.user?.full_name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="font-medium text-sm truncate">{annonce.user?.full_name}</p>
                          {annonce.user?.email_verified && (
                            <BadgeCheck className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {annonce.location}
                        </p>
                      </div>
                    </div>

                    {/* Title & Description */}
                    <h3 className="font-semibold mb-2 line-clamp-1">{annonce.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {annonce.description}
                    </p>

                    {/* Categories */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge className="bg-primary text-xs">
                        {annonce.offer_category?.icon} Propose: {annonce.offer_category?.name}
                      </Badge>
                      {annonce.open_to_all ? (
                        <Badge variant="secondary" className="text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Ouvert à tout
                        </Badge>
                      ) : annonce.want_categories_details?.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          Cherche: {annonce.want_categories_details.slice(0, 2).map(c => c.name).join(', ')}
                          {annonce.want_categories_details.length > 2 && '...'}
                        </Badge>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm text-muted-foreground pt-3 border-t">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {annonce.duration_hours}h
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {annonce.views_count || 0} vues
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucune annonce trouvee</h3>
              <p className="text-muted-foreground mb-4">
                Soyez le premier a publier une annonce !
              </p>
              {isAuthenticated && (
                <Link to="/annonces/new">
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer une annonce
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
